import React, { useEffect, useState, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { shipmentApi, dashboardApi } from '../../services/api';
import api from '../../services/api';
import { StatusBadge, fmtDT, fmtPKR, SERVICE_LABELS, addrStr } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, Pagination } from '../../components/common';
import { Search, RefreshCw, AlertTriangle, Package, Scale } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

// ── Operations Dashboard ──────────────────────────────────────────────────────
export function OpsDashboard() {
  const [data, setData]       = useState(null);
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const dash = await dashboardApi.operations().catch(() => ({ success:false, data:null }));
      if (dash.success && dash.data) {
        setData(dash.data);
        // Cross-dock alerts are now embedded in dashboard response
        if (dash.data.exceptions?.crossdock > 0) {
          // Fetch details only when there are alerts
          api.get('/hubs/crossdock-alerts').then(r=>r.data)
            .then(r=>{ if(r.success) setAlerts(r.data||[]); })
            .catch(()=>{});
        }
      }
    } catch(e) {
      console.error('Ops dashboard error:', e);
    } finally { setLoading(false); }
  };

  // Load once on mount - manual refresh via button
  useEffect(() => { load(); }, []);

  return (
    <AppShell title="Operations Dashboard" actions={
      <button className="btn btn-outline btn-sm" onClick={load} aria-label="Refresh"><RefreshCw size={14}/></button>
    }>
      {loading && !data ? <Spinner /> : data ? (
        <>
          <div className="stat-grid" style={{ marginBottom:20 }}>
            <MiniStat label="Awaiting Pickup"  v={data.pendingActions?.awaiting_pickup}  color="var(--info)" />
            <MiniStat label="At Hub"           v={data.pendingActions?.at_hub}           color="var(--navy)" />
            <MiniStat label="Out for Delivery" v={data.pendingActions?.out_for_delivery} color="var(--amber)" />
            <MiniStat label="Delivered Today"  v={data.todayStats?.find(s=>s.status==='DELIVERED')?.count||0} color="var(--success)" />
            <MiniStat label="Scan Gaps"        v={data.exceptions?.scan_gaps||0}         color="var(--danger)" />
          </div>

          {/* OPS-FE-006: Cross-dock idle alert > 2h */}
          {alerts.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom:16, display:'flex', gap:10 }}>
              <AlertTriangle size={18} style={{ flexShrink:0, marginTop:2 }}/>
              <div>
                <strong>Cross-dock Alert</strong> — {alerts.length} shipment{alerts.length>1?'s':''} idle at hub &gt; 2 hours
                <div style={{ marginTop:6, display:'flex', flexWrap:'wrap', gap:6 }}>
                  {alerts.slice(0,5).map(a=>(
                    <Link key={a.tracking_id} to={`/track/${a.tracking_id}`} style={{ fontFamily:'monospace', fontSize:'.78rem', color:'var(--navy)', background:'var(--gray-100)', padding:'2px 8px', borderRadius:4 }}>
                      {a.tracking_id}
                    </Link>
                  ))}
                  {alerts.length>5 && <span className="text-sm text-muted">+{alerts.length-5} more</span>}
                </div>
              </div>
            </div>
          )}

          {/* OPS-FE-007: Hub scan gap alert */}
          {data.exceptions?.scan_gaps > 0 && (
            <div className="alert alert-danger" style={{ marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
              <AlertTriangle size={18}/>
              <span><strong>Hub Scan Gap:</strong> {data.exceptions.scan_gaps} shipment{data.exceptions.scan_gaps>1?'s':''} missing expected hub scan.</span>
            </div>
          )}

          {/* Today's manifests */}
          <div className="card" style={{ marginBottom:20 }}>
            <div className="card-header">
              <span className="card-title">Today's Manifests</span>
              <Link to="/ops/manifests" className="btn btn-outline btn-sm">View All</Link>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Code</th><th>Type</th><th>Driver</th><th>Vehicle</th><th>Shipments</th><th>Departure</th><th>Status</th></tr></thead>
                <tbody>
                  {(data.manifests||[]).length ? data.manifests.map(m=>(
                    <tr key={m.manifest_id}>
                      <td><code style={{ fontSize:'.8rem', fontWeight:700 }}>{m.code}</code></td>
                      <td>{m.route_type}</td>
                      <td>{m.driver_name||'—'}</td>
                      <td>{m.vehicle_plate||'—'}</td>
                      <td style={{ fontWeight:600 }}>{m.total_shipments}</td>
                      <td className="text-sm">{fmtDT(m.planned_departure)}</td>
                      <td><span className={`badge ${m.status==='SEALED'?'badge-navy':m.status==='DEPARTED'?'badge-info':m.status==='DELIVERED'?'badge-success':'badge-warning'}`}>{m.status}</span></td>
                    </tr>
                  )) : <tr><td colSpan={7} style={{ textAlign:'center', padding:20, color:'var(--gray-400)' }}>No manifests today</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:12 }}>
            {[{ label:'Scan Terminal', icon:'📷', to:'/ops/scan' },{ label:'Book Shipment', icon:'📦', to:'/ops/booking' },{ label:'Manifests', icon:'📋', to:'/ops/manifests' },{ label:'All Shipments', icon:'🚚', to:'/ops/shipments' }].map(q=>(
              <Link key={q.to} to={q.to} style={{ textDecoration:'none' }}>
                <div className="stat-card" style={{ textAlign:'center', cursor:'pointer' }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>{q.icon}</div>
                  <div className="stat-label">{q.label}</div>
                </div>
              </Link>
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign:'center', padding:60, color:'var(--gray-400)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🏭</div>
          <div style={{ fontWeight:600, marginBottom:8 }}>No operations data yet</div>
          <div style={{ fontSize:'.85rem', marginBottom:16 }}>Start scanning or booking shipments to populate this dashboard</div>
          <button className="btn btn-outline btn-sm" onClick={load}>↻ Retry</button>
        </div>
      )}
    </AppShell>
  );
}

// ── Operations Shipments ──────────────────────────────────────────────────────
export function OpsShipments() {
  const [shipments, setShipments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [filters, setFilters]   = useState({ status:'', serviceType:'', search:'' });
  const [showWeight, setShowWeight] = useState(false);
  const [weightCheck, setWeightCheck] = useState({ tracking:'', declared:0, verified:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await shipmentApi.getOps({ page, limit:50, status:filters.status||undefined, serviceType:filters.serviceType||undefined, search:filters.search||undefined });
      if (res.success) { setShipments(res.data); setPagination(res.pagination||{}); }
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { const t=setTimeout(load,300); return()=>clearTimeout(t); }, [load]);

  const BIN_MAP = { DOM_EXP:'EXPRESS-A', DOM_SD:'EXPRESS-A', DOM_STD:'STD-B', DOM_ECO:'BULK-C', INT_AIR:'INTL-D', INT_SEA:'INTL-D', INT_EXP:'INTL-D' };

  const openWeightCheck = (s) => {
    setWeightCheck({ tracking:s.tracking_id, declared:parseFloat(s.weight_actual), verified:'' });
    setShowWeight(true);
  };

  const confirmWeight = async () => {
    const v = parseFloat(weightCheck.verified);
    if (!v) { toast.error('Enter verified weight'); return; }
    const discPct = Math.abs(weightCheck.declared - v) / weightCheck.declared;
    const note = `Weight check: declared ${weightCheck.declared}kg, verified ${v}kg (${(discPct*100).toFixed(1)}% diff)`;
    await api.patch(`/shipments/${weightCheck.tracking}/status`, {
      status: shipments.find(s=>s.tracking_id===weightCheck.tracking)?.status,
      notes: note,
    }).catch(()=>{});
    if (discPct > 0.20) {
      toast.warning(`⚠️ Discrepancy ${(discPct*100).toFixed(0)}% — flagged for review`);
    } else {
      toast.success('Weight confirmed ✓');
    }
    setShowWeight(false);
    load();
  };

  return (
    <AppShell title="Shipments" actions={<Link to="/ops/booking" className="btn btn-primary btn-sm"><Package size={14}/> New Booking</Link>}>
      <div className="filters-bar">
        <div className="search-input">
          <Search size={14} aria-hidden="true"/>
          <input aria-label="Search shipments" placeholder="Tracking ID or customer…" value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))}/>
        </div>
        <select className="form-control" style={{ width:180 }} aria-label="Filter by status" value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
          <option value="">All Statuses</option>
          {['BOOKING_CONFIRMED','PICKED_UP','IN_TRANSIT','AT_HUB','OUT_FOR_DELIVERY','DELIVERED','DELIVERY_ATTEMPTED','RTO_INITIATED','CANCELLED'].map(s=>
            <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
          )}
        </select>
        <select className="form-control" style={{ width:150 }} aria-label="Filter by service" value={filters.serviceType} onChange={e=>setFilters(f=>({...f,serviceType:e.target.value}))}>
          <option value="">All Services</option>
          {Object.entries(SERVICE_LABELS).map(([v,l])=><option key={v} value={v}>{l}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : shipments.length ? (
            <table aria-label="Operations shipments">
              <thead><tr><th>Tracking ID</th><th>Service</th><th>Customer</th><th>Destination</th><th>Weight</th><th>COD</th><th>Bin</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {shipments.map(s=>(
                  <tr key={s.tracking_id}>
                    <td><Link to={`/track/${s.tracking_id}`} style={{ fontFamily:'monospace', fontSize:'.8rem', color:'var(--navy)', fontWeight:700 }}>{s.tracking_id}</Link></td>
                    <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{s.service_type}</span></td>
                    <td>
                      <div className="text-sm" style={{ fontWeight:500 }}>{s.customer_name}</div>
                      <div className="text-sm text-muted">{s.customer_mobile}</div>
                    </td>
                    <td className="text-sm">{addrStr(s.delivery_address)}</td>
                    <td className="text-sm">{s.weight_actual}kg</td>
                    <td>{s.cod_amount>0?<span style={{ fontWeight:600, color:'var(--amber)' }}>{fmtPKR(s.cod_amount)}</span>:'—'}</td>
                    <td><span style={{ fontFamily:'monospace', fontSize:'.72rem', background:'var(--gray-100)', padding:'2px 6px', borderRadius:4 }}>{BIN_MAP[s.service_type]||'STD-B'}</span></td>
                    <td><StatusBadge status={s.status}/></td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <Link to={`/track/${s.tracking_id}`} className="btn btn-outline btn-sm" style={{ fontSize:'.7rem', padding:'3px 8px' }}>View</Link>
                        {['AT_HUB','BOOKING_CONFIRMED','PICKED_UP'].includes(s.status) && (
                          <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem', padding:'3px 8px' }} onClick={()=>openWeightCheck(s)}>
                            <Scale size={11}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📦" title="No shipments found"/>}
        </div>
        <Pagination page={page} pages={pagination.pages||1} onPage={setPage}/>
      </div>

      {/* Weight check modal (OPS-FE-010) */}
      <Modal open={showWeight} onClose={()=>setShowWeight(false)} title="Weight Verification">
        <div className="modal-body">
          <div className="alert alert-info" style={{ marginBottom:12 }}>
            Tolerance: ±20% (±100g minimum). Discrepancy &gt;20% triggers fraud review flag.
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Declared Weight</label>
              <input className="form-control" readOnly value={`${weightCheck.declared} kg`}/>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="vw">Verified Weight (kg) <span className="req">*</span></label>
              <input id="vw" className="form-control" type="number" step="0.001" placeholder="0.000" aria-required="true"
                value={weightCheck.verified} onChange={e=>setWeightCheck(w=>({...w,verified:e.target.value}))}/>
            </div>
          </div>
          {weightCheck.verified && (
            <div className={`alert ${Math.abs(weightCheck.declared-parseFloat(weightCheck.verified))/weightCheck.declared>0.20?'alert-danger':'alert-success'}`}>
              Difference: {Math.abs(((weightCheck.declared-parseFloat(weightCheck.verified))/weightCheck.declared)*100).toFixed(1)}%
              {Math.abs(weightCheck.declared-parseFloat(weightCheck.verified))/weightCheck.declared>0.20
                ? ' — EXCEEDS 20% TOLERANCE' : ' — Within tolerance ✓'}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowWeight(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={confirmWeight}>Confirm Weight</button>
        </div>
      </Modal>
    </AppShell>
  );
}

function MiniStat({ label, v, color }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color:color||'var(--navy)', fontSize:'1.6rem' }}>{v||0}</div>
    </div>
  );
}
